<template>
  <div class="flex h-[calc(100vh-7rem)] min-h-155 overflow-hidden rounded-md border bg-background">
    <ConversationListPanel />

    <section class="hidden min-w-0 flex-1 flex-col md:flex">
      <template v-if="activeConversation">
        <header class="flex h-16 shrink-0 items-center justify-between border-b px-5">
          <div class="min-w-0">
            <h2 class="truncate text-base font-semibold">
              {{ conversationTitle(activeConversation) }}
            </h2>
            <p class="text-xs text-muted-foreground">
              {{ activeParticipants.length }} active participants · {{ activeOnlineUserIds.length }} online
            </p>
          </div>
          <div class="flex items-center gap-1">
            <Button variant="ghost" size="icon-sm" :disabled="messageStore.isLoadingMessages" @click="reloadMessages">
              <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': messageStore.isLoadingMessages }" />
            </Button>
            <Button variant="ghost" size="icon-sm" :aria-label="detailsPanelOpen ? 'Hide conversation details' : 'Show conversation details'" :title="detailsPanelOpen ? 'Hide conversation details' : 'Show conversation details'" @click="toggleDetailsPanel">
              <PanelRightClose v-if="detailsPanelOpen" class="h-4 w-4" />
              <PanelRightOpen v-else class="h-4 w-4" />
            </Button>
          </div>
        </header>

        <Alert v-if="messageActionError" variant="destructive" class="m-4 mb-0">
          <AlertDescription>{{ messageActionError }}</AlertDescription>
        </Alert>

        <ChatMessageThread
          :conversation-id="activeConversation.id"
          :messages="activeMessages"
          :current-user-id="authStore.user?.id"
          :participants="activeParticipants"
          :loading="messageStore.isLoadingMessages"
          @load-older="loadOlderMessages"
          @message-action-started="clearMessageActionError"
          @message-action-failed="setMessageActionError"
        />
        <ChatMessageComposer :conversation-id="activeConversation.id" />
      </template>

      <div v-else class="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        {{ messageActionError || 'Loading conversation.' }}
      </div>
    </section>

    <ConversationDetailsPanel
      v-if="activeConversation && detailsPanelOpen"
      :conversation="activeConversation"
      :title="conversationTitle(activeConversation)"
      :current-user-id="authStore.user?.id"
      :online-user-ids="activeOnlineUserIds"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { PanelRightClose, PanelRightOpen, RefreshCw } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../resources/js/components/ui/alert/index.js'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import { useAuthStore } from '../../../../../../resources/js/stores/auth.js'
import ConversationDetailsPanel from '../components/conversations/ConversationDetailsPanel.vue'
import ConversationListPanel from '../components/conversations/ConversationListPanel.vue'
import ChatMessageComposer from '../components/messages/ChatMessageComposer.vue'
import ChatMessageThread from '../components/messages/ChatMessageThread.vue'
import { useChatStore } from '../stores/chatStore.js'
import { useConversationParticipantStore } from '../stores/conversationParticipantStore.js'
import { useConversationStore } from '../stores/conversationStore.js'
import { useMessageStore } from '../stores/messageStore.js'
import { usePresenceUserStore } from '../stores/presenceUserStore.js'

const route = useRoute()
const authStore = useAuthStore()
const chatStore = useChatStore()
const conversationStore = useConversationStore()
const messageStore = useMessageStore()
const participantStore = useConversationParticipantStore()
const presenceUserStore = usePresenceUserStore()

const messageActionError = ref('')
const detailsPanelOpen = ref(false)

const routeConversationId = computed(() => {
  const conversationId = Number(route.params.conversation)

  return Number.isInteger(conversationId) && conversationId > 0 ? conversationId : null
})

const activeConversation = computed(() => conversationStore.getConversationById(routeConversationId.value))
const activeMessages = computed(() => messageStore.getMessagesByConversationId(routeConversationId.value))
const activeOnlineUserIds = computed(() => presenceUserStore.getOnlineUserIdsByConversationId(routeConversationId.value))
const activeParticipants = computed(() => {
  return activeConversation.value?.participants?.filter((participant) => participant.left_at === null) || []
})

const openConversationFromRoute = async () => {
  messageActionError.value = ''

  if (!routeConversationId.value) {
    chatStore.clearActiveConversation()
    messageActionError.value = 'Unable to open this conversation.'
    return
  }

  try {
    chatStore.setActiveConversation(routeConversationId.value)
    chatStore.subscribeToConversation(routeConversationId.value)

    if (!activeConversation.value) {
      await conversationStore.fetchConversation(routeConversationId.value)
    }

    await messageStore.fetchMessages(routeConversationId.value, { clear: true })
    await participantStore.markConversationRead(routeConversationId.value)
    await nextTick()
  } catch (error) {
    messageActionError.value = errorMessageFrom(error, 'Unable to open this conversation.')
  }
}

const reloadMessages = async () => {
  if (!routeConversationId.value) {
    return
  }

  messageActionError.value = ''

  try {
    await messageStore.fetchMessages(routeConversationId.value, { clear: true })
  } catch (error) {
    messageActionError.value = errorMessageFrom(error, 'Unable to reload messages.')
  }
}

const loadOlderMessages = async () => {
  if (!routeConversationId.value) {
    return
  }

  messageActionError.value = ''

  try {
    await messageStore.fetchOlderMessages(routeConversationId.value)
  } catch (error) {
    messageActionError.value = errorMessageFrom(error, 'Unable to load older messages.')
  }
}

const toggleDetailsPanel = () => {
  detailsPanelOpen.value = !detailsPanelOpen.value
}

const clearMessageActionError = () => {
  messageActionError.value = ''
}

const setMessageActionError = (message) => {
  messageActionError.value = message
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages[0]
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

const conversationTitle = (conversation) => {
  if (conversation.title) {
    return conversation.title
  }

  const otherParticipant = conversation.participants?.find((participant) => participant.user_id !== authStore.user?.id)

  return otherParticipant?.user?.name || 'Direct conversation'
}

const isPersistedMessageId = (messageId) => {
  const numericMessageId = Number(messageId)

  return Number.isInteger(numericMessageId) && numericMessageId > 0
}

watch(
  routeConversationId,
  () => {
    detailsPanelOpen.value = false
    openConversationFromRoute()
  },
  { immediate: true }
)

watch(
  () => activeMessages.value.at(-1)?.id,
  async (messageId) => {
    if (!routeConversationId.value || !isPersistedMessageId(messageId)) {
      return
    }

    await participantStore.markConversationRead(routeConversationId.value, {
      message_id: messageId,
    })
  }
)

onUnmounted(() => {
  if (Number(chatStore.activeConversationId) === routeConversationId.value) {
    chatStore.clearActiveConversation()
  }
})
</script>
